import { FC, useEffect, useState, useCallback } from 'react'
import {
	Table,
	Button,
	Select,
	Tag,
	Popconfirm,
	message,
	Space,
	Tooltip
} from 'antd'
import { PlusOutlined, DeleteOutlined, WifiOutlined } from '@ant-design/icons'
import { DeviceInstallation } from '../helpers/types/inventory'
import {
	fetchDeviceInstallations,
	deleteDeviceInstallation
} from '../api/inventory'
import { carrierStore } from '../store/carrier'
import { Link, useNavigate } from 'react-router-dom'
import { RNCarriers } from '../helpers/routeNames'

const RISK_COLOR: Record<string, string> = {
	low: 'success',
	medium: 'warning',
	high: 'error'
}

const InventoryInstallations: FC = () => {
	const { carriers } = carrierStore()
	const navigate = useNavigate()
	const [carrierId, setCarrierId] = useState<number | null>(null)
	const [installs, setInstalls] = useState<DeviceInstallation[]>([])
	const [loading, setLoading] = useState(false)

	const load = useCallback(async (cId: number) => {
		setLoading(true)
		try {
			const data = await fetchDeviceInstallations(cId)
			setInstalls(data)
		} catch {
			message.error('Failed to load installations')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		if (carrierId) load(carrierId)
		else setInstalls([])
	}, [carrierId, load])

	const handleDelete = async (id: number) => {
		try {
			await deleteDeviceInstallation(id)
			setInstalls((prev) => prev.filter((d) => d.id !== id))
		} catch {
			message.error('Error')
		}
	}

	const columns = [
		{
			title: 'Carrier',
			key: 'carrier',
			render: () => {
				const c = carriers.find((c) => c.id === carrierId)
				return c ? (
					<Link to={`${RNCarriers}/${c.id}#inventory-device`}>
						{c.name}
					</Link>
				) : '—'
			}
		},
		{
			title: 'Date',
			dataIndex: 'installDate',
			key: 'installDate',
			render: (v: string | null) =>
				v ? new Date(v).toLocaleDateString('ru') : '—'
		},
		{
			title: 'Device',
			key: 'device',
			render: (_: any, r: DeviceInstallation) => (
				<Space size={4}>
					{r.deviceInstalled ? (
						<>
							<WifiOutlined style={{ color: '#3894EB' }} />
							{r.deviceSerial || 'Installed'}
						</>
					) : (
						<Tag color="default">None</Tag>
					)}
				</Space>
			)
		},
		{
			title: 'SIM',
			key: 'sim',
			render: (_: any, r: DeviceInstallation) =>
				r.simInstalled ? (
					<Tag color="cyan">{r.simNumber || 'Installed'}</Tag>
				) : (
					'—'
				)
		},
		{
			title: 'Technician',
			dataIndex: 'technician',
			key: 'technician',
			render: (v: string | null) => v || '—'
		},
		{
			title: 'Vibration',
			dataIndex: 'vibrationRisk',
			key: 'vibrationRisk',
			render: (v: string | null) =>
				v ? <Tag color={RISK_COLOR[v]}>{v}</Tag> : '—'
		},
		{
			title: 'Heat',
			dataIndex: 'heatRisk',
			key: 'heatRisk',
			render: (v: string | null) =>
				v ? <Tag color={RISK_COLOR[v]}>{v}</Tag> : '—'
		},
		{
			title: 'Mount',
			dataIndex: 'mountType',
			key: 'mountType',
			render: (v: string | null) => v || '—'
		},
		{
			title: '',
			key: 'actions',
			width: 60,
			render: (_: any, rec: DeviceInstallation) => (
				<Popconfirm
					title="Delete?"
					onConfirm={() => handleDelete(rec.id)}
					okButtonProps={{ danger: true }}>
					<Button
						icon={<DeleteOutlined />}
						size="small"
						type="text"
						danger
					/>
				</Popconfirm>
			)
		}
	]

	return (
		<div>
			<header className="page-header">
				<h1 className="page-h1">IoT Device Installations</h1>
			</header>
			<main>
				<Space
					style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap' }}
					size={8}>
					<Select
						placeholder="Select carrier"
						style={{ width: 220 }}
						allowClear
						showSearch
						optionFilterProp="label"
						value={carrierId}
						onChange={(v) => setCarrierId(v ?? null)}
						options={carriers.map((c) => ({
							label: c.name,
							value: c.id
						}))}
					/>
					<Tooltip title={!carrierId ? 'Select a carrier first' : ''}>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							disabled={!carrierId}
							onClick={() =>
								carrierId &&
								navigate(`${RNCarriers}/${carrierId}#inventory-device`)
							}>
							Add Record
						</Button>
					</Tooltip>
				</Space>

				<Table
					dataSource={installs}
					columns={columns}
					rowKey="id"
					loading={loading}
					pagination={{ pageSize: 20 }}
					locale={{
						emptyText: carrierId
							? 'No installation records'
							: 'Select a carrier to view records'
					}}
				/>
			</main>
		</div>
	)
}

export default InventoryInstallations
